import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { topics } from "@/mocks/topics";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "事件合集", to: "/collections" },
  { label: "常见问题", to: "/faq" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const solid = scrolled || !isHome;

  const results = keyword.trim()
    ? topics
        .filter(
          (t) =>
            t.title.includes(keyword.trim()) ||
            t.category.includes(keyword.trim()) ||
            t.tags.some((tag) => tag.includes(keyword.trim())),
        )
        .slice(0, 5)
    : [];

  const submitSearch = () => {
    if (!keyword.trim()) return;
    navigate(`/collections?q=${encodeURIComponent(keyword.trim())}`);
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-eye-2-line text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-lg font-bold tracking-tight ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                蝌蚪黑料
              </span>
              <span
                className={`text-[10px] tracking-[0.2em] mt-0.5 ${
                  solid ? "text-foreground-500" : "text-background-200"
                }`}
              >
                热点事件追踪
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active =
                link.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                    solid
                      ? active
                        ? "text-primary-600 font-medium bg-primary-50"
                        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      : active
                        ? "text-background-50 font-medium"
                        : "text-background-100 hover:text-background-50"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <div ref={searchRef} className="relative hidden md:block">
              <div
                className={`flex items-center gap-2 h-10 px-3 rounded-full border transition-colors ${
                  solid
                    ? "bg-background-100 border-background-200 focus-within:border-primary-300"
                    : "bg-background-50/20 border-background-50/40 focus-within:border-background-50"
                }`}
              >
                <i
                  className={`ri-search-line text-sm ${
                    solid ? "text-foreground-500" : "text-background-100"
                  }`}
                ></i>
                <input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                  placeholder="搜索事件或话题"
                  className={`w-32 lg:w-44 bg-transparent text-sm outline-none ${
                    solid
                      ? "text-foreground-950 placeholder:text-foreground-400"
                      : "text-background-50 placeholder:text-background-200"
                  }`}
                />
              </div>
              {searchOpen && keyword.trim() && (
                <div className="absolute right-0 mt-2 w-72 rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                  {results.length > 0 ? (
                    <>
                      {results.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => {
                            navigate(`/topic/${t.id}`);
                            setSearchOpen(false);
                            setKeyword("");
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-background-100 transition-colors border-b border-background-100 last:border-0"
                        >
                          <p className="text-sm text-foreground-900 line-clamp-1">{t.title}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">{t.category}</p>
                        </button>
                      ))}
                      <button
                        onClick={submitSearch}
                        className="w-full text-left px-4 py-2.5 text-xs text-primary-600 hover:bg-primary-50 transition-colors"
                      >
                        查看全部搜索结果 <i className="ri-arrow-right-line"></i>
                      </button>
                    </>
                  ) : (
                    <p className="px-4 py-6 text-center text-sm text-foreground-500">
                      暂无匹配话题，换个关键词试试
                    </p>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/collections"
              className={`hidden sm:inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                solid
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-background-50 text-foreground-950 hover:bg-background-100"
              }`}
            >
              <i className="ri-flashlight-line"></i>
              看热榜
            </Link>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开导航菜单"
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md ${
                solid ? "text-foreground-900" : "text-background-50"
              }`}
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 h-11 px-3 rounded-full bg-background-100 border border-background-200">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitSearch();
                    setMenuOpen(false);
                  }
                }}
                placeholder="搜索事件或话题"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
            </div>
          </div>
          <nav className="flex flex-col px-2 pb-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-3 text-sm text-foreground-800 hover:bg-background-100 rounded-md"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}